import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { countryCodes, CountryCode } from '../../utils/country-codes';
import * as lpn from 'google-libphonenumber';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RecaptchaComponent } from '../../recaptcha/recaptcha.component';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    RecaptchaComponent,
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css'],
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  passwordStrength = 0;
  showPassword = false;
  showConfirmPassword = false;
  registrationComplete = false;
  countryCodes: CountryCode[] = countryCodes;
  selectedCountryCode: string = '+389';
  selectedCountry: CountryCode | undefined;
  isMobile = false;
  private phoneUtil = lpn.PhoneNumberUtil.getInstance();
  private destroy$ = new Subject<void>();
  recaptchaToken = '';
  recaptchaErrorMessage = '';

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;
  }

  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.value;
      if (!password) {
        return null;
      }

      const errors: ValidationErrors = {};

      if (password.length < 8) {
        errors['minlength'] = {
          requiredLength: 8,
          actualLength: password.length,
        };
      }

      if (!/[A-Z]/.test(password)) {
        errors['uppercase'] = true;
      }

      if (!/[0-9]/.test(password)) {
        errors['number'] = true;
      }

      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors['specialChar'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const cleanValue = control.value.replace(/\D/g, '');
      if (cleanValue !== control.value) {
        return { pattern: true };
      }

      try {
        const regionCode = this.getRegionCode(this.selectedCountryCode);
        if (!regionCode) return { invalidNumber: true };

        const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
          control.value,
          regionCode
        );
        if (!this.phoneUtil.isValidNumber(phoneNumber)) {
          return { invalidNumber: true };
        }
        return null;
      } catch (e) {
        return { invalidNumber: true };
      }
    };
  }

  private getRegionCode(countryCode: string): string | null {
    const code = countryCode.replace('+', '');
    const country = this.countryCodes.find((c) => c.dialCode === countryCode);
    return country ? country.code : null;
  }

  ngOnInit(): void {
    this.checkScreenSize();

    const macedoniaCode = this.countryCodes.find((c) => c.code === 'MK');
    if (macedoniaCode) {
      this.selectedCountryCode = macedoniaCode.dialCode;
      this.selectedCountry = macedoniaCode;
    }

    this.initializeForm();
    this.setupFormSubscriptions();
    this.animateFormEntry();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.registerForm = this.formBuilder.group(
      {
        first_name: ['', [Validators.required, Validators.minLength(2)]],
        last_name: ['', [Validators.required, Validators.minLength(2)]],
        email: ['', [Validators.required, Validators.email]],
        country_code: [this.selectedCountryCode, [Validators.required]],
        phone_number: [
          '',
          [Validators.pattern(/^\d+$/), this.phoneNumberValidator()],
        ],
        password: ['', [Validators.required, this.passwordValidator()]],
        confirm_password: ['', Validators.required],
        marketing_consent: [false],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  private setupFormSubscriptions(): void {
    this.registerForm
      .get('password')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((password) => {
        this.calculatePasswordStrength(password);
      });

    this.registerForm
      .get('country_code')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectedCountryCode = value;
        this.selectedCountry = this.countryCodes.find(
          (c) => c.dialCode === value
        );

        const phoneControl = this.registerForm.get('phone_number');
        phoneControl?.updateValueAndValidity();
      });
  }

  private animateFormEntry(): void {
    setTimeout(() => {
      const formElement = document.querySelector('.register-form');
      if (formElement) {
        formElement.classList.add('visible');
      }
    }, 100);
  }

  getPhoneExampleNumber(): string {
    try {
      const regionCode = this.getRegionCode(this.selectedCountryCode);
      if (!regionCode) return '';

      const example = this.phoneUtil.getExampleNumber(regionCode);
      if (!example) return '';

      return this.phoneUtil.format(example, lpn.PhoneNumberFormat.NATIONAL);
    } catch (e) {
      return '';
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  calculatePasswordStrength(password: string): void {
    if (!password) {
      this.passwordStrength = 0;
      return;
    }

    let strength = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    if (checks.length) strength += 25;
    if (checks.uppercase) strength += 25;
    if (checks.number) strength += 25;
    if (checks.special) strength += 25;

    this.passwordStrength = Math.max(0, Math.min(100, strength));
  }

  calculateStrengthPercentage(): string {
    return `${this.passwordStrength}%`;
  }

  getStrengthLabel(): string {
    if (this.passwordStrength < 25) return 'Weak';
    if (this.passwordStrength < 50) return 'Fair';
    if (this.passwordStrength < 75) return 'Good';
    return 'Strong';
  }

  getStrengthColor(): string {
    if (this.passwordStrength < 25) return '#64748b';
    if (this.passwordStrength < 50) return '#475569';
    if (this.passwordStrength < 75) return '#334155';
    return '#1e293b';
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirm_password');

    if (password?.value !== confirmPassword?.value) {
      confirmPassword?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmPassword?.errors) {
        if (Object.keys(confirmPassword.errors).length > 1) {
          const { passwordMismatch, ...otherErrors } = confirmPassword.errors;
          confirmPassword.setErrors(
            Object.keys(otherErrors).length ? otherErrors : null
          );
        } else {
          confirmPassword.setErrors(null);
        }
      }
      return null;
    }
  }

  get first_name() {
    return this.registerForm.get('first_name');
  }

  get last_name() {
    return this.registerForm.get('last_name');
  }

  get email() {
    return this.registerForm.get('email');
  }

  get country_code() {
    return this.registerForm.get('country_code');
  }

  get phone_number() {
    return this.registerForm.get('phone_number');
  }

  get password() {
    return this.registerForm.get('password');
  }

  get confirm_password() {
    return this.registerForm.get('confirm_password');
  }

  get marketing_consent() {
    return this.registerForm.get('marketing_consent');
  }

  formatPhoneNumber(): string | null {
    const countryCode = this.country_code?.value;
    const localNumber = this.phone_number?.value;

    if (!countryCode || !localNumber) {
      return null;
    }

    try {
      const regionCode = this.getRegionCode(countryCode);
      if (!regionCode) return `${countryCode}${localNumber}`;

      const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
        localNumber,
        regionCode
      );
      return this.phoneUtil.format(phoneNumber, lpn.PhoneNumberFormat.E164);
    } catch (e) {
      return `${countryCode}${localNumber}`;
    }
  }

  private validateAllFormFields(): void {
    Object.keys(this.registerForm.controls).forEach((key) => {
      const control = this.registerForm.get(key);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  onRecaptchaChange(token: string): void {
    this.recaptchaToken = token;
    this.recaptchaErrorMessage = '';
  }

  onRecaptchaError(error: string): void {
    this.recaptchaToken = '';
    this.recaptchaErrorMessage = error;
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.validateAllFormFields();

      if (this.isMobile) {
        const firstErrorElement = document.querySelector('.form-error');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
      return;
    }

    if (!this.recaptchaToken) {
      this.recaptchaErrorMessage = 'Please complete the security verification';
      return;
    }

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const userData: any = {
      first_name: this.first_name?.value?.trim(),
      last_name: this.last_name?.value?.trim(),
      email: this.email?.value?.trim().toLowerCase(),
      password: this.password?.value,
      marketing_consent: this.marketing_consent?.value || false,
      recaptchaToken: this.recaptchaToken,
    };

    const formattedPhoneNumber = this.formatPhoneNumber();
    if (formattedPhoneNumber) {
      userData.phone_number = formattedPhoneNumber;
    }

    this.authService.register(userData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage = 'Registration successful! Redirecting...';

        setTimeout(() => {
          this.router.navigate(['/verify-email'], {
            queryParams: {
              email: this.email?.value,
              type: 'registration',
            },
          });
        }, 2000);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = this.getErrorMessage(error);

        if (this.isMobile) {
          const errorElement = document.querySelector('.alert-error');
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      },
    });
  }

  private getErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.status === 409) {
      return 'An account with this email already exists.';
    }

    if (error?.status === 400) {
      return 'Please check your information and try again.';
    }

    if (error?.status === 0) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    return 'Registration failed. Please try again.';
  }
}
